import { MapPinned, ArrowRight } from 'lucide-react';

import type { SiteContent } from '../../content/types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Container } from '../layout/container';

interface HeroSectionProps {
  content: SiteContent;
}

export function HeroSection({ content }: HeroSectionProps): JSX.Element {
  return (
    <section className="relative overflow-hidden py-14 sm:py-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary-soft),transparent_55%)]" />
      <Container className="relative grid gap-8 lg:grid-cols-[1.25fr_0.85fr] lg:items-start">
        <div className="space-y-6">
          <Badge className="w-fit bg-[color:var(--color-accent)] text-[color:var(--color-secondary)]">{content.hero.eyebrow}</Badge>
          <h1 className="max-w-3xl text-balance text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">{content.hero.headline}</h1>
          <p className="max-w-2xl text-pretty text-base leading-relaxed text-[color:var(--color-muted)] sm:text-lg">{content.hero.subheadline}</p>

          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg">
              <a href={content.hero.primaryCta.href}>{content.hero.primaryCta.label}</a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={content.hero.secondaryCta.href}>{content.hero.secondaryCta.label}</a>
            </Button>
            <Button asChild size="lg" variant="ghost">
              <a href={content.hero.tertiaryCta.href} className="inline-flex items-center gap-2">
                <MapPinned className="h-4 w-4" />
                {content.hero.tertiaryCta.label}
              </a>
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {content.hero.badges.map((badge) => (
              <Badge key={badge} variant="outline" className="bg-white/80">
                {badge}
              </Badge>
            ))}
          </div>
        </div>

        <Card className="border-none bg-[linear-gradient(155deg,var(--color-surface)_0%,var(--color-primary-soft)_130%)] shadow-calm">
          <CardContent className="space-y-4 p-6 sm:p-7">
            {content.hero.quickFacts.map((fact) => (
              <div key={fact.label} className="rounded-xl border border-white/70 bg-white/70 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-muted)]">{fact.label}</p>
                <p className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--color-text)]">
                  {fact.value}
                  <ArrowRight className="h-4 w-4 text-[color:var(--color-primary)]" />
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </Container>
    </section>
  );
}
